import PadletApi from 'surface/padlet_api'
import uniqueId from 'lodash.uniqueid'
import debounce from 'lodash.debounce'
import { Commit, Dispatch } from 'vuex'
import { captureMessage } from 'bits/error_tracker'
import { __ } from 'bits/intl'
import { FetchError } from 'surface/api_fetch'
import { Id, Cid, PostAttributes, Post } from 'types'
import cloneDeep from 'lodash.clonedeep'
import PromiseQueue from 'bits/promise_queue'

// HELPERS
const isNew = (post: Post | PostAttributes): boolean => !post.id
const isEmpty = (post: PostAttributes): boolean => (!post.subject && !post.body && !post.attachment)
const isNonEmpty = (post: PostAttributes): boolean => !isEmpty(post)

interface CommitAndDispatch {
  commit: Commit
  dispatch: Dispatch
}

interface CopyXferPostParams {
  wallId?: Id
  wallDescription?: string
  wallTitle?: string
  wallSectionId?: Id
  isDestinationThisWall?: boolean
  postPosition?: { left: number, top: number  }
  postId: Id,
  transferredPostCid?: Cid,
}

const DEFAULT_RETRY_DELAY = 500
function wrapMethodWithRetries<T> (method: () => Promise<T>, maxRetries = 0, delayInMs = DEFAULT_RETRY_DELAY): () => Promise<T> {
  let retriesLeft = maxRetries

  return (): Promise<T> => new Promise((resolve, reject): void => {
    async function methodWithRetries (): Promise<any> {
      try {
        const result = await method()
        return resolve(result)
      } catch (e) {
        const isClientError = e.status >= 400 && e.status < 500
        const toRetry = !isClientError && retriesLeft > 0
        if (toRetry) {
          retriesLeft -= 1
          setTimeout((): void => { methodWithRetries() }, delayInMs)
        } else {
          reject(e)
        }
        return null
      }
    }
    methodWithRetries()
  })
}

// Cached PostSavers
const postSaverById: { [id: number]: PostSaver } = {}
const postSaverByCid: { [cid: string]: PostSaver } = {}

// These are the standard requests. You may define your own strings as custom keys.
enum PostRequestKey {
  Create = 'Create',
  // Update post with attributes at the time when request is enqueued
  UpdateCurrent = 'UpdateCurrent',
  // Update post with attributes at the time when request is executed
  UpdateLatest = 'UpdateLatest',
}

interface CustomSavePostRequest {
  key: string,
  method: (post?: Post) => Promise<any>,
}

/*****************************************************

PostSaver ensures that only one post update/create event is being made at a time.
After instantiation, you may call `save` on it, or enqueue a request:

```
const saver = new PostSaver(post, dispatch)
saver.save() // create immediately if new
// ...
saver.save() // does not fire request if one is still in progress
// ...
saver.save() // updates post if post has been persisted
saver.save() // debounces updates
saver.customSave({ key: 'A_POST_RELATED_REQUEST', method: () => { ... } }) // custom request queued
saver.save() // update request is queue after custom request
```

*****************************************************/

class PostSaver {
  private q: PromiseQueue
  private dispatch!: Dispatch
  private getters!: any
  // Store both id and cid so that we can use the same queue before and after the post is saved
  private postId!: Id | null
  private postCid!: Cid
  private _debouncedEnqueueUpdate!: null | (() => void) // lazily created

  public constructor ({ id, cid }: Post, { dispatch: PostModuleDispatch, getters: PostModuleGetters }) {
    this.postId = id || null
    this.postCid = cid
    this.dispatch = PostModuleDispatch
    this.getters = PostModuleGetters
    this.q = new PromiseQueue()
  }

  public save (): Post {
    const post = this.post
    if (isEmpty(post)) return post
    if (isNew(post) && (this.q.currentKey !== PostRequestKey.Create)) {
      this.q.enqueue(PostRequestKey.Create, this.createPost.bind(this))
    } else {
      this.debouncedEnqueueUpdate()
    }
    return post
  }

  public customSave ({ key, method }: CustomSavePostRequest): Promise<any> {
    // Ensure post has been is created
    if (isNew(this.post) && (this.q.currentKey !== PostRequestKey.Create)) {
      this.q.enqueue(PostRequestKey.Create, this.createPost.bind(this))
    }
    // Post should be updated to current state before the new request is made
    // Convert UpdateLatest request to an UpdateCurrent request to lock in the current state
    if (this.q.lastQueuedKey === PostRequestKey.UpdateLatest) {
      this.q.unqueue()
      const currentPost = cloneDeep(this.post)
      const updateCurrentPost = (): Promise<any> => this.updatePost(currentPost)
      this.q.enqueue(PostRequestKey.UpdateCurrent, updateCurrentPost)
    }

    // Wrap method to pass in latest post. Request might require id but was queued before post was saved.
    const wrappedMethod = (): Promise<any> => method(this.post)

    // Finally, queue the method.
    return this.q.enqueue(key, wrappedMethod)
  }

  // Get the latest post
  private get post (): Post {
    const postFromCid = this.getters.postEntitiesByCid[this.postCid]
    if (postFromCid) return postFromCid

    // Update postCid if it has changed
    const postFromId = this.getters.getPostByServerId(this.postId)
    delete postSaverByCid[this.postCid]
    this.postCid = postFromId.cid
    return postFromId
  }

  private get debouncedEnqueueUpdate (): () => void {
    if (!this._debouncedEnqueueUpdate) {
      this._debouncedEnqueueUpdate = debounce(this.enqueueUpdate, 500)
    }
    return this._debouncedEnqueueUpdate
  }

  private enqueueUpdate (): void {
    if (this.q.lastQueuedKey === PostRequestKey.UpdateLatest) return
    this.q.enqueue(PostRequestKey.UpdateLatest, this.updatePost.bind(this))
  }

  private async createPost (): Promise<any> {
    try {
      const createPostWithRetries = wrapMethodWithRetries((): Promise<Post> => PadletApi.Wish.create(this.post), 1)
      const savedPost = await createPostWithRetries()
      this.dispatch('createdPostSuccess', { ...savedPost, ...this.post })
      this.postId = savedPost.id as Id
      postSaverById[savedPost.id as Id] = this
      return savedPost
    } catch (e) {
      if (e.status === 403) {
        this.dispatch('showReloadNotification', null, { root: true }) // Ask to reload if no permissions
      } else {
        this.showSaveFailureSnackbar()
      }
      const context = { response: e.message, attachment: this.post.attachment }
      captureMessage(`CreatedPostError ${e.status || ''}`, { context })
      return null
    }
  }

  private async updatePost (post?: Post): Promise<any> {
    // If not specific version of the post is provided, fetch the latest from the store
    const clientPost = post || this.post
    if (isEmpty(clientPost)) return null
    try {
      const savedPost = await PadletApi.Wish.update(clientPost) // Already retries
      this.dispatch('updatedPostSuccess', { ...savedPost, ...clientPost })
      return savedPost
    } catch (e) {
      if (e.status === 404) {
        this.dispatch('refresh', null, { root: true }) // Refresh if post deleted
      } else if (e.status === 403) {
        this.dispatch('showReloadNotification', null, { root: true }) // Ask to reload if no permissions
      } else {
        this.showSaveFailureSnackbar()
      }
      const context = { response: e.message, postId: clientPost.id }
      captureMessage(`UpdatedPostError ${e.status || ''}`, { context })
      return null
    }
  }

  private showSaveFailureSnackbar (): void {
    const options = {
      message: __("Sorry, we couldn't save your changes. Please refresh the page."),
      actionText: __('Refresh'),
      actionName: 'reloadPage',
    }
    this.dispatch('snackbars/setSnackbar', options, { root: true })
  }
}

// Assumes post has a Cid.
function getOrCreatePostSaver (post: Post, context): PostSaver {
  // Prefer to look up post by server id since cid changes after initial creation.
  // Assume that when post is successfully saved, it will be populated in postSaverById.
  let saver = (post.id && postSaverById[post.id]) || postSaverByCid[post.cid]
  if (saver) return saver

  saver = new PostSaver(post, context)
  if (post.id) {
    postSaverById[post.id] = saver
  } else {
    postSaverByCid[post.cid] = saver
  }
  return saver
}

const savePost = (context, post: PostAttributes): Post => {
  const { commit, getters } = context
  // Ensure that: (1) post had cid, (2) post is added to store, (3) post in store is latest version
  commit(isNew(post) ? 'CREATED_POST' : 'UPDATED_POST', post)
  const postWithCid = post.cid ? post : getters.getPostByServerId(post.id)
  return getOrCreatePostSaver(postWithCid, context).save()
}

function customSavePost (context, post: Post, request: CustomSavePostRequest): Promise<any> {
  return getOrCreatePostSaver(post, context).customSave(request)
}

/**
 * Delete post using PadletApi abstraction
 */
async function deletePost ({ commit, dispatch }: CommitAndDispatch, postToBeSaved: Post): Promise<boolean> {
  try {
    commit('DELETED_POST', postToBeSaved)
    const obj = await PadletApi.Wish.delete({
      id: postToBeSaved.id,
    })
    commit('DELETED_POST_SUCCESS', { ...obj, cid: postToBeSaved.cid })
    return true
  } catch (e) {
    if (e.status === 404) {
      dispatch('refresh', null, { root: true })
      return true
    }
    if (e.status === 403) {
      dispatch('showReloadNotification', null, { root: true })
      return false
    }
    commit('DELETED_POST_ERROR', postToBeSaved)
    const context = {
      response: e.message,
      postId: postToBeSaved.id,
    }
    captureMessage(`DeletedPostError ${e.status || ''}`, { context })
    return false
  }
}

/**
 * Delete all posts using PadletApi abstraction
 */
function deleteAllPosts ({ commit }: {commit: Commit}, posts: Post[]): void {
  posts.forEach((post): void => {
    PadletApi.Wish.delete({
      id: post.id,
    }).then((obj: Post): void => {
      commit('DELETED_POST', post)
      commit('DELETED_POST_SUCCESS', { ...obj, cid: post.cid })
    }).catch((e: FetchError): void => {
      commit('DELETED_POST_ERROR', post)
      const context = {
        response: e.message,
        action: 'deleteAllPosts',
      }
      captureMessage(`DeletedPostError ${e.status || ''}`, { context })
    })
  })
}

async function copyPost ({ dispatch }, params: CopyXferPostParams): Promise<void> {
  const copyFunc = PadletApi.Wish.copy.bind(PadletApi.Wish)
  const { wallId, wallDescription, wallTitle, wallSectionId, postPosition } = params
  try {
    const postAttributes = await copyFunc({
      wishId: params.postId,
      wallId,
      wallDescription,
      wallTitle,
      wallSectionId,
      postPosition,
    })
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch('postAction/updatePostCopied', {
      newPostData: postAttributes,
      originalPostId: params.postId,
      isDestinationNewWall,
    }, { root: true })
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      dispatch('postAction/updatePostCopied', { error: parsedError }, { root: true })
    }
  }
}

async function transferPost ({ dispatch }, params: CopyXferPostParams): Promise<void> {
  const { wallId, wallDescription, wallTitle, wallSectionId, isDestinationThisWall, transferredPostCid } = params
  const transferFunc = PadletApi.Wish.transfer.bind(PadletApi.Wish)
  try {
    // can transfer intra-wall, between shelf columns
    const postAttributes = await transferFunc({
      wishId: params.postId,
      wallId,
      wallDescription,
      wallTitle,
      wallSectionId,
      // postPosition,
    })
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch('postAction/updatePostTransferred', {
      newPostData: postAttributes,
      originalPostId: params.postId,
      isDestinationThisWall,
      isDestinationNewWall,
      transferredPostCid,
    }, { root: true })
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      dispatch('postAction/updatePostTransferred', { error: parsedError }, { root: true })
    }
  }
}

/**
 * Get the next temp client ID for the next new post being added to
 * client-side list of posts.
 */
function nextTempClientId (): string {
  return uniqueId('c_new')
}

function generateClientId (post: PostAttributes): string {
  if (post.id) {
    return 'c' + post.id
  }
  return nextTempClientId()
}

function getClientId (post: Post): string|null {
  if (post.cid) {
    return post.cid
  }
  if (post.id) {
    return 'c' + post.id
  }
  return null
}
function isValidClientId (cid: Cid): boolean {
  if (!String(cid).startsWith('c')) {
    return false
  }
  return true
}

export {
  deletePost,
  deleteAllPosts,
  copyPost,
  transferPost,
  savePost,
  generateClientId,
  getClientId,
  isValidClientId,
  isNew,
  isEmpty,
  isNonEmpty,
  customSavePost,
  CustomSavePostRequest,
}
